import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { SECTORS, SECTOR_COLORS } from "@/data/sectorConfig";
import { SavingsCalculator } from "@/components/SavingsCalculator";
import { SectorWizard } from "@/components/SectorWizard";
import { ArrowLeft, ChevronRight, CheckCircle2, HelpCircle, ChevronDown } from "lucide-react";
import { ResidentialSolarChoice } from "@/components/BalconySolarSection";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left">
        <span className="text-sm font-semibold text-slate-900 pr-4">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-sm text-slate-500 pb-4 leading-relaxed">{a}</p>}
    </div>
  );
};

export default function SectorPage({ sectorId }) {
  const navigate = useNavigate();
  const [showWizard, setShowWizard] = useState(false);
  const [wizardFormData, setWizardFormData] = useState(null);
  const sector = SECTORS[sectorId];
  const c = SECTOR_COLORS[sector.color];
  const Icon = sector.icon;

  const openRooftopWizard = () => {
    setWizardFormData(null);
    setShowWizard(true);
  };

  const openBalconyWizard = () => {
    setWizardFormData({ property_type: "appartamento", apt_installation: "balcone" });
    setShowWizard(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
    setWizardFormData(null);
  };

  useSEO({
    title: `${sector.title} | Fotovoltaico Chiavi in Mano`,
    description: sector.subtitle,
    canonical: `${window.location.origin}/${sectorId}`,
  });

  return (
    <div className="min-h-screen bg-white" data-testid={`sector-page-${sectorId}`}>
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Fotovoltaico Chiavi in Mano" className="h-14 w-auto object-contain" />
            <span className="hidden sm:block text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Fotovoltaico Chiavi in Mano</span>
          </a>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1.5 transition-colors" data-testid="sector-back">
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
            <button onClick={openRooftopWizard} className={`${c.bg} hover:opacity-90 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale shadow-lg`} data-testid="sector-cta-top">
              Richiedi Preventivo
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="sector-hero">
        <div className="absolute inset-0">
          <img src={sector.heroImage} alt={sector.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className={`inline-flex items-center gap-2 ${c.light} ${c.text} text-sm font-semibold px-3 py-1.5 rounded-full mb-4`}>
            <Icon className="w-4 h-4" /> {sector.label}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight max-w-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
            {sector.title}
          </h1>
          <p className="mt-5 text-lg text-white/70 max-w-xl">{sector.subtitle}</p>
          <button onClick={openRooftopWizard} className={`mt-8 inline-flex items-center gap-2 ${c.bg} hover:opacity-90 text-white font-bold rounded-full px-8 py-3.5 text-sm btn-scale shadow-xl`} data-testid="sector-hero-cta">
            Richiedi Preventivo Gratuito <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className={`${c.light} py-8`} data-testid="sector-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {sector.stats.map((s, i) => (
            <div key={i}>
              <div className={`text-2xl sm:text-3xl font-bold ${c.text}`} style={{ fontFamily: "Outfit, sans-serif" }}>{s.value}</div>
              <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 sm:py-20" data-testid="sector-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: "Outfit, sans-serif" }}>
            Perche Scegliere il Fotovoltaico {sector.label}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {sector.benefits.map((b, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors">
                <CheckCircle2 className={`w-6 h-6 ${c.text} shrink-0 mt-0.5`} />
                <div>
                  <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{b.title}</h3>
                  <p className="text-sm text-slate-500">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solar Choice - Tetto vs Balcone (only for residential) */}
      {sectorId === "residenziale" && (
        <ResidentialSolarChoice onRooftopClick={openRooftopWizard} onBalconyClick={openBalconyWizard} />
      )}

      {/* Calculator */}
      <SavingsCalculator initialSector={sectorId} onConfigureClick={openRooftopWizard} />

      {/* FAQ */}
      <section className="py-16 sm:py-20" data-testid="sector-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8 flex items-center justify-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <HelpCircle className="w-6 h-6 text-slate-400" /> Domande Frequenti
          </h2>
          <div className="bg-white rounded-2xl border border-slate-100 px-6">
            {sector.faq.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 text-center">
        <p className="text-xs text-slate-500">&copy; 2025 Fotovoltaico Chiavi in Mano. Tutti i diritti riservati.</p>
      </footer>

      {showWizard && (
        <SectorWizard
          initialSector={sectorId}
          initialFormData={wizardFormData}
          onClose={closeWizard}
        />
      )}
    </div>
  );
}
